import React, { useEffect, useId, useLayoutEffect, useRef, useState } from 'react';
import { ArrowRight } from '../../design/icons';
import { CHAT_COMMANDS } from './chatCommands';
import { textLayout, clearTextLayoutCache } from '../../lib/textLayout';

interface ChatComposerProps {
    value: string;
    onChange: (value: string) => void;
    onSend: (text: string) => void;
    loading: boolean;
    inputRef: React.RefObject<HTMLTextAreaElement>;
    /** Newest command first. */
    commandHistory: string[];
}

/** Terminal input with command discovery and ordinary multiline editing. */
export default function ChatComposer({ value, onChange, onSend, loading, inputRef, commandHistory }: ChatComposerProps) {
    const id = useId();
    const fieldId = `chatbot-command-${id}`;
    const listId = `chatbot-commands-${id}`;
    const hintId = `chatbot-keyboard-hint-${id}`;
    const listRef = useRef<HTMLDivElement>(null);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const [dismissedValue, setDismissedValue] = useState<string | null>(null);
    const historyIndex = useRef(-1);
    const historyDraft = useRef('');
    const historyValue = useRef<string | null>(null);
    const composing = useRef(false);
    const resizeRef = useRef<() => void>(() => {});

    useEffect(() => {
        let live = true;
        document.fonts?.ready.then(() => {
            if (live) { clearTextLayoutCache(); resizeRef.current(); }
        });
        return () => { live = false; };
    }, []);

    const prefix = value.toLowerCase();
    const matches = /^\/[^\s]*$/.test(prefix)
        ? CHAT_COMMANDS.filter(item => item.command.startsWith(prefix))
        : [];
    const exactCommand = matches.some(item => item.command === prefix);
    const showCommands = !loading && matches.length > 0 && !exactCommand && dismissedValue !== value;
    const activeIndex = Math.min(selectedIndex, matches.length - 1);

    useLayoutEffect(() => {
        const field = inputRef.current;
        if (!field) return;
        const resize = () => {
            const style = window.getComputedStyle(field);
            const lineHeight = parseFloat(style.lineHeight) || 22;
            const padding = (parseFloat(style.paddingTop) || 0) + (parseFloat(style.paddingBottom) || 0);
            const border = (parseFloat(style.borderTopWidth) || 0) + (parseFloat(style.borderBottomWidth) || 0);
            const maxHeight = lineHeight * 4 + padding + border;
            const width = field.clientWidth - (parseFloat(style.paddingLeft) || 0) - (parseFloat(style.paddingRight) || 0) - border;
            let textHeight = lineHeight;
            if (width > 0) {
                // Measure cached text segments, not scrollHeight after writing
                // height:auto. This composer is shared by every site route.
                try {
                    const font = `${style.fontWeight} ${style.fontSize || '14px'} ${style.fontFamily || 'monospace'}`;
                    textHeight = textLayout(value.endsWith('\n') ? `${value} ` : value || ' ', font, width, lineHeight, 'pre-wrap').height;
                } catch { textHeight = Math.max(lineHeight, field.scrollHeight - padding); }
            }
            const height = Math.max(lineHeight, textHeight) + padding + border;
            field.style.height = `${Math.min(height, maxHeight)}px`;
            field.style.maxHeight = `${maxHeight}px`;
            field.style.overflowY = height > maxHeight ? 'auto' : 'hidden';
        };
        resizeRef.current = resize;
        resize();
        if (typeof ResizeObserver === 'undefined') return;
        let width = field.clientWidth;
        const observer = new ResizeObserver(() => {
            if (field.clientWidth === width) return;
            width = field.clientWidth;
            resize();
        });
        observer.observe(field);
        return () => observer.disconnect();
    }, [value, inputRef]);

    useEffect(() => {
        setSelectedIndex(0);
        if (historyValue.current !== value) {
            historyIndex.current = -1;
            historyValue.current = null;
        }
    }, [value]);

    useEffect(() => {
        const list = listRef.current;
        const selected = list?.querySelector<HTMLElement>('[aria-selected="true"]');
        if (!list || !selected) return;
        const itemBounds = selected.getBoundingClientRect();
        const listBounds = list.getBoundingClientRect();
        if (itemBounds.top < listBounds.top) list.scrollTop += itemBounds.top - listBounds.top;
        if (itemBounds.bottom > listBounds.bottom) list.scrollTop += itemBounds.bottom - listBounds.bottom;
    }, [activeIndex, showCommands]);

    const selectCommand = (command: string) => {
        setDismissedValue(command);
        historyIndex.current = -1;
        onChange(command);
        inputRef.current?.focus({ preventScroll: true });
    };

    const submit = () => {
        if (loading || !value.trim() || composing.current) return;
        historyIndex.current = -1;
        historyValue.current = null;
        setDismissedValue(null);
        onSend(value.trim());
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
        // Safari may report isComposing=false for the final IME Enter.
        if (composing.current || event.nativeEvent.isComposing || event.keyCode === 229) return;
        if (showCommands) {
            if (event.key === 'Escape') {
                event.preventDefault();
                event.stopPropagation();
                setDismissedValue(value);
                return;
            }
            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                event.preventDefault();
                const direction = event.key === 'ArrowDown' ? 1 : -1;
                setSelectedIndex((activeIndex + direction + matches.length) % matches.length);
                return;
            }
            if ((event.key === 'Enter' && !event.shiftKey) || (event.key === 'Tab' && !event.shiftKey)) {
                event.preventDefault();
                selectCommand(matches[activeIndex].command);
                return;
            }
        }
        if (event.key === 'Enter' && !event.shiftKey) {
            event.preventDefault();
            submit();
            return;
        }
        // Do not steal caret movement from a draft or a multiline message.
        const browsingHistory = historyIndex.current >= 0 && value === historyValue.current;
        if ((!browsingHistory && (value.includes('\n') || value.trim())) || !commandHistory.length) return;
        if (event.key === 'ArrowUp') {
            event.preventDefault();
            if (!browsingHistory) historyDraft.current = value;
            const next = Math.min(historyIndex.current + 1, commandHistory.length - 1);
            historyIndex.current = next;
            historyValue.current = commandHistory[next];
            setDismissedValue(commandHistory[next]);
            onChange(commandHistory[next]);
        } else if (event.key === 'ArrowDown' && browsingHistory) {
            event.preventDefault();
            const next = historyIndex.current - 1;
            const nextValue = next < 0 ? historyDraft.current : commandHistory[next];
            historyIndex.current = next;
            historyValue.current = next < 0 ? null : nextValue;
            setDismissedValue(nextValue);
            onChange(nextValue);
        }
    };

    return (
        <form className="chatbot-composer" onSubmit={event => { event.preventDefault(); submit(); }}>
            {showCommands && (
                <div ref={listRef} id={listId} className="chatbot-composer__commands" role="listbox" aria-label="Command suggestions">
                    {matches.map((item, index) => (
                        <button key={item.command} id={`${listId}-${index}`} type="button" role="option"
                            aria-selected={index === activeIndex} tabIndex={-1}
                            className={`chatbot-composer__command${index === activeIndex ? ' is-selected' : ''}`}
                            onMouseDown={event => event.preventDefault()}
                            onClick={() => selectCommand(item.command)}>
                            <span className="chatbot-composer__command-name">{item.command}</span>
                            <span className="chatbot-composer__command-description">{item.description}</span>
                        </button>
                    ))}
                </div>
            )}
            <div className="chatbot-composer__input">
                <span className="chatbot-composer__prompt" aria-hidden="true">❯</span>
                <label className="chatbot-panel__sr-only" htmlFor={fieldId}>Message to assistant</label>
                <textarea id={fieldId} ref={inputRef} className="chatbot-composer__field"
                    value={value} rows={1} placeholder="Ask a question or type / for commands"
                    aria-describedby={hintId} aria-autocomplete="list"
                    aria-controls={showCommands ? listId : undefined}
                    aria-activedescendant={showCommands ? `${listId}-${activeIndex}` : undefined}
                    onChange={event => { setDismissedValue(null); onChange(event.target.value); }}
                    onKeyDown={handleKeyDown}
                    onCompositionStart={() => { composing.current = true; }}
                    onCompositionEnd={() => { composing.current = false; }}
                    autoComplete="off" spellCheck={false} />
                <button type="submit" className="chatbot-composer__send" disabled={loading || !value.trim()}
                    aria-label="Send message"><span>Send</span><ArrowRight size={16} /></button>
            </div>
            <p id={hintId} className="chatbot-composer__hint">
                {showCommands ? '↑ ↓ choose · Enter or Tab complete · Esc dismiss' : 'Enter send · Shift+Enter new line · / commands'}
            </p>
        </form>
    );
}
